import React from 'react';
import { useSocket } from '../contexts/SocketContext';
import { useRiderRoster } from '../game/riderRoster';
import { useLiveMultiplier } from '../game/useLiveMultiplier';
import { canModerateRider, maskPlayerName, shownRiderChroma } from '../utils/displayName';
import { useHiddenRiders } from '../utils/hiddenRiders';
import { RiderActionsButton } from './RiderActions';
import { formatCompactMoney } from '../utils/money';
import { CHROMA_PALETTES } from './entities/SkierEntity';

const SLOTS = 3;

/**
 * Phones only (index.css): the top riders on the slope, just under the game,
 * because the full Live bets list sits below the wager panel there. It replaces
 * the leaderboard that used to be drawn over the run itself.
 */
const TopRidersStrip: React.FC = () => {
  const { gameInfo, player } = useSocket();
  const { entries, queued } = useRiderRoster();
  useHiddenRiders();
  const multiplier = useLiveMultiplier();
  const phase = gameInfo?.phase;
  const onSlope = entries.filter(entry => entry.status !== 'queued').slice(0, SLOTS);
  if ((phase !== 'IN_PROGRESS' && phase !== 'CRASHED') || (onSlope.length === 0 && queued === 0)) return null;

  return (
    <section className="top-riders" aria-label="Top riders">
      <ol>
        {onSlope.map(entry => {
          const color = CHROMA_PALETTES[shownRiderChroma(entry.chroma, entry.playerId) ?? entry.chroma]?.label ?? 'var(--ice)';
          const value = entry.status === 'cashed_out' && entry.cashoutMultiplier !== null ? `✓ ${entry.cashoutMultiplier.toFixed(2)}x`
            : entry.status === 'lost' ? 'lost'
            : formatCompactMoney(entry.betAmount * multiplier);
          const actions = canModerateRider(entry.playerId, player?.id);
          return (
            <li key={entry.playerId} className={`top-rider is-${entry.status}${actions ? ' has-actions' : ''}`}>
              <span className="top-rider-dot" style={{ background: color }} aria-hidden="true" />
              <span className="top-rider-name">{maskPlayerName(entry.username, entry.playerId)}</span>
              <span className="top-rider-value">{value}</span>
              {actions && <RiderActionsButton playerId={entry.playerId} username={entry.username} />}
            </li>
          );
        })}
      </ol>
      {queued > 0 && <span className="top-riders-queue">+{queued} waiting</span>}
    </section>
  );
};

export default TopRidersStrip;
